import type { Lens } from '@hookform/lenses';
import { z } from 'zod/v4';
import { Accordion } from '@interxfi/design-system/components/accordion/accordion';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CARD_NO_PADDING } from '@interxfi/design-system/components/card/card';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxmUserauthV1ProgramControls } from '@interxfi/types/generated/api';
import { useFormErrorAccordion } from '../../../../../components/card-group/form/accordion-form-group';
import type { FormConfirmationGroup } from '../../../../../components/form/confirmation/form-confirmation';
import { getChangedKeyIds } from '../../../../../util/get-changed-key-ids';
import { type ProgramControlSection, programControlSections } from '../../controls/program-control-sections';
import { ProgramControlSummary } from '../../controls/program-control-summary';
import {
  formValuesToProgramAccountNumbersControls,
  getInitialFormValuesForProgramAccountNumbersControls,
  ProgramAccountNumbersControlsForm,
  programControlsAccountNumbersFormDefaultValues,
  programControlsAccountNumbersFormSchema,
} from './account-numbers/program-controls-account-numbers-form';
import {
  formValuesToProgramApiAuthControls,
  getInitialFormValuesForProgramApiAuthControls,
  ProgramApiAuthControlsForm,
  programControlsApiAuthFormDefaultValues,
  programControlsApiAuthFormSchema,
} from './api-auth/program-controls-api-auth-form';
import {
  formValuesToProgramBatchOrderControls,
  getInitialFormValuesForProgramBatchOrderControls,
  ProgramBatchOrderControlsForm,
  programControlsBatchOrderFormDefaultValues,
  programControlsBatchOrderFormSchema,
} from './batch-order/program-controls-batch-order-form';
import {
  formValuesToProgramBooksControls,
  getInitialFormValuesForProgramBooksControls,
  ProgramBooksControlsForm,
  programControlsBooksFormDefaultValues,
  programControlsBooksFormSchema,
} from './books/program-controls-books-form';
import { formValuesToProgramBatchFundingControls } from './funding/program-controls-batch-funding-form';
import {
  formValuesToProgramFundingControls,
  getInitialFormValuesForProgramFundingControls,
  ProgramFundingControlsForm,
  programControlsFundingFormDefaultValues,
  programControlsFundingFormSchema,
} from './funding/program-controls-funding-form';
import {
  formValuesToProgramInstantDepositControls,
  getInitialFormValuesForProgramInstantDepositControls,
  ProgramInstantDepositControlsForm,
  programControlsInstantDepositFormDefaultValues,
  programControlsInstantDepositFormSchema,
} from './instant-deposit/program-controls-instant-deposit-form';
import {
  formValuesToProgramOrdersControls,
  getInitialFormValuesForProgramOrdersControls,
  ProgramOrderControlsForm,
  programOrderControlsFormDefaultValues,
  programOrderControlsFormSchema,
} from './orders/program-controls-orders-form';
import {
  formValuesToProgramRegistrationControls,
  getInitialFormValuesForProgramRegistrationControls,
  ProgramRegistrationControlsForm,
  programControlsRegistrationFormDefaultValues,
  programControlsRegistrationFormSchema,
} from './registration/program-controls-registration-form';
import {
  formValuesToProgramSweepsControls,
  getInitialFormValuesForProgramSweepsControls,
  ProgramControlsSweepsForm,
  programControlsSweepsFormDefaultValues,
  programControlsSweepsFormSchema,
} from './sweeps/program-controls-sweeps-form';

export const programControlsFormSchema = z.object({
  apiAuth: programControlsApiAuthFormSchema,
  books: programControlsBooksFormSchema,
  funding: programControlsFundingFormSchema,
  orders: programOrderControlsFormSchema,
  registration: programControlsRegistrationFormSchema,
  sweeps: programControlsSweepsFormSchema,
  instantDeposit: programControlsInstantDepositFormSchema,
  batchOrder: programControlsBatchOrderFormSchema,
  accountNumbers: programControlsAccountNumbersFormSchema,
});

export type ProgramControlsFormValues = z.infer<typeof programControlsFormSchema>;

export const defaultProgramControlsFormValues: ProgramControlsFormValues = {
  apiAuth: programControlsApiAuthFormDefaultValues,
  books: programControlsBooksFormDefaultValues,
  funding: programControlsFundingFormDefaultValues,
  orders: programOrderControlsFormDefaultValues,
  registration: programControlsRegistrationFormDefaultValues,
  sweeps: programControlsSweepsFormDefaultValues,
  instantDeposit: programControlsInstantDepositFormDefaultValues,
  batchOrder: programControlsBatchOrderFormDefaultValues,
  accountNumbers: programControlsAccountNumbersFormDefaultValues,
};

export function getInitialFormValuesForProgramControls(data: IxmUserauthV1ProgramControls | undefined) {
  if (!data) {
    return defaultProgramControlsFormValues;
  }

  return {
    apiAuth: getInitialFormValuesForProgramApiAuthControls(data?.apiAuth),
    books: getInitialFormValuesForProgramBooksControls(data?.books),
    funding: getInitialFormValuesForProgramFundingControls(data?.funding, data?.batchFunding),
    orders: getInitialFormValuesForProgramOrdersControls(data?.order),
    registration: getInitialFormValuesForProgramRegistrationControls(data?.registration),
    sweeps: getInitialFormValuesForProgramSweepsControls(data?.sweeps),
    instantDeposit: getInitialFormValuesForProgramInstantDepositControls(data?.instantDeposit),
    batchOrder: getInitialFormValuesForProgramBatchOrderControls(data?.batchOrder),
    accountNumbers: getInitialFormValuesForProgramAccountNumbersControls(data?.accountNumbers),
  };
}

export function formValuesToProgramControls(values: ProgramControlsFormValues): IxmUserauthV1ProgramControls {
  return {
    apiAuth: formValuesToProgramApiAuthControls(values.apiAuth),
    books: formValuesToProgramBooksControls(values.books),
    funding: formValuesToProgramFundingControls(values.funding),
    batchFunding: formValuesToProgramBatchFundingControls(values.funding.batch),
    order: formValuesToProgramOrdersControls(values.orders),
    registration: formValuesToProgramRegistrationControls(values.registration),
    sweeps: formValuesToProgramSweepsControls(values.sweeps),
    instantDeposit: formValuesToProgramInstantDepositControls(values.instantDeposit),
    batchOrder: formValuesToProgramBatchOrderControls(values.batchOrder),
    accountNumbers: formValuesToProgramAccountNumbersControls(values.accountNumbers),
  };
}

const programControlSectionByFormKey = {
  registration: programControlSections.registration,
  sweeps: programControlSections.sweeps,
  apiAuth: programControlSections.developers,
  funding: programControlSections.funding,
  accountNumbers: programControlSections.accountNumbers,
  instantDeposit: programControlSections.instantDeposit,
  orders: programControlSections.orders,
  batchOrder: programControlSections.batchOrder,
  books: programControlSections.books,
} as const satisfies Record<keyof ProgramControlsFormValues, ProgramControlSection>;

export function getChangedProgramControlSections(baseline: ProgramControlsFormValues, next: ProgramControlsFormValues): string[] {
  return getChangedKeyIds(baseline, next, programControlSectionByFormKey);
}

export function mapProgramControlsToFormConfirmationGroup(
  values: IxmUserauthV1ProgramControls | undefined,
  programId: string | undefined,
  withTitle?: boolean,
  defaultOpenSections?: string[],
): FormConfirmationGroup {
  return {
    id: 'controls',
    type: 'node',
    cardProps: {
      isBorderless: true,
      padding: CARD_NO_PADDING,
    },
    node: (
      <VerticalStack gap={7}>
        {withTitle ? <Typography.Level3>Program Controls</Typography.Level3> : null}
        {/* TODO: TS error */}
        <ProgramControlSummary controls={values} defaultOpenSections={defaultOpenSections} isPending={false} programId={programId} />
      </VerticalStack>
    ),
  };
}

interface ProgramControlsFormProps {
  lens: Lens<ProgramControlsFormValues>;
  programId: string | undefined;
}

const sectionTitles = {
  registration: 'Registration',
  sweeps: 'Sweeps',
  apiAuth: 'Developers',
  funding: 'Funding',
  accountNumbers: 'Account Numbers',
  instantDeposit: 'Instant Deposit',
  orders: 'Orders',
  batchOrder: 'Batch Orders',
  books: 'Books',
} as const;

export function ProgramControlsForm({ lens, programId }: ProgramControlsFormProps) {
  const accordionProps = useFormErrorAccordion(lens, sectionTitles);

  return (
    <Accordion type="multiple" {...accordionProps}>
      <ProgramRegistrationControlsForm lens={lens.focus('registration')} />
      <ProgramControlsSweepsForm lens={lens.focus('sweeps')} programId={programId} />
      <ProgramApiAuthControlsForm lens={lens.focus('apiAuth')} />
      <ProgramFundingControlsForm lens={lens.focus('funding')} />
      <ProgramAccountNumbersControlsForm lens={lens.focus('accountNumbers')} />
      <ProgramInstantDepositControlsForm lens={lens.focus('instantDeposit')} />
      <ProgramOrderControlsForm lens={lens.focus('orders')} />
      <ProgramBatchOrderControlsForm lens={lens.focus('batchOrder')} />
      <ProgramBooksControlsForm lens={lens.focus('books')} />
    </Accordion>
  );
}
